import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getAdminViewMode, setAdminViewMode } from '../utils/adminViewMode';

const COOLDOWN_MS = 30 * 1000; // 30s per OTP send

async function getAccessibleClientIdsForUser(userId) {
  const [{ data: cliRows }, { data: regRows }] = await Promise.all([
    supabase.from('admin_user_clients').select('client_id').eq('user_id', userId),
    supabase.from('admin_user_regions').select('region_id').eq('user_id', userId),
  ]);
  const clientIds = new Set((cliRows || []).map(r => String(r.client_id)));
  const regionIds = (regRows || []).map(r => r.region_id);
  if (regionIds.length > 0) {
    const { data: regionMap } = await supabase
      .from('regions')
      .select('id, client_id')
      .in('id', regionIds);
    (regionMap || []).forEach(r => clientIds.add(String(r.client_id)));
  }
  return Array.from(clientIds).filter(Boolean);
}

function MagicLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);

  const LS_KEY_GLOBAL = 'magic_login.lastSentAt';

  // If a session is present (after magic link), route to correct destination
  useEffect(() => {
    let mounted = true;
    const handle = async () => {
      console.log('[MAGIC][LOGIN] mount check session');
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session || null;
      console.log('[MAGIC][LOGIN] session', { hasSession: !!session, user: session?.user?.email });
      if (!mounted || !session?.user?.email) return;
      try {
        // Global?
        const { data: lean } = await supabase
          .from('lean_admin_users')
          .select('is_global')
          .eq('user_id', session.user.id)
          .eq('is_global', true)
          .maybeSingle();
        console.log('[MAGIC][LOGIN] post-session lean_admin_users', { lean });
        if (lean) {
          const mode = getAdminViewMode();
          navigate(mode === 'manager' ? '/manager' : '/admin/clients', { replace: true });
          return;
        }
        const ids = await getAccessibleClientIdsForUser(session.user.id);
        if (ids.length === 1) {
          navigate(`/manager?client=${ids[0]}`, { replace: true });
          return;
        }
        navigate('/manager', { replace: true });
      } catch {}
    };
    handle();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, _s) => { handle(); });
    return () => { mounted = false; sub?.subscription?.unsubscribe(); };
  }, [navigate]);

  // Cooldown ticker from localStorage
  useEffect(() => {
    const tick = () => {
      try {
        const last = Number(localStorage.getItem(LS_KEY_GLOBAL) || '0');
        if (!last) { setCooldown(0); return; }
        const remain = Math.max(0, Math.ceil((last + COOLDOWN_MS - Date.now()) / 1000));
        setCooldown(remain);
      } catch { setCooldown(0); }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  const handleSendLink = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    // Manager login path defaults global users into manager-view mode.
    setAdminViewMode('manager');
    const targetEmail = String(email || '').trim().toLowerCase();
    if (!targetEmail) { setError('Email is required'); return; }
    // Rate limit: global cooldown between sends
    const last = Number(localStorage.getItem(LS_KEY_GLOBAL) || '0');
    if (last && Date.now() - last < COOLDOWN_MS) {
      const remain = Math.ceil((last + COOLDOWN_MS - Date.now()) / 1000);
      setError(`Please wait ${remain}s before requesting another link.`);
      return;
    }
    setLoading(true);
    try {
      console.log('[MAGIC][LOGIN] start', { email: targetEmail });
      // Gate via SECURITY DEFINER RPCs to bypass RLS before session
      const { data: isAllowed, error: allowErr } = await supabase
        .rpc('is_any_admin_email', { target_email: targetEmail });
      console.log('[MAGIC][LOGIN] is_any_admin_email', { isAllowed, allowErr });
      if (allowErr || !isAllowed) {
        setError('This email is not authorized for admin access.');
        setLoading(false);
        return;
      }
      // Send OTP code email (passwordless)
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: targetEmail,
        options: {
          shouldCreateUser: false,
        },
      });
      console.log('[MAGIC][LOGIN] send OTP result', { otpErr });
      if (otpErr) {
        setError(otpErr.message || 'Failed to send code');
        setLoading(false);
        return;
      }
      setCodeSent(true);
      setInfo('We sent a 6-digit code to your email.');
      try { localStorage.setItem(LS_KEY_GLOBAL, String(Date.now())); } catch {}
    } catch (e) {
      setError('Unexpected error sending code');
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    const targetEmail = String(email || '').trim().toLowerCase();
    const token = String(otp || '').trim();
    if (!targetEmail || token.length < 6) {
      setError('Enter the 6-digit code');
      return;
    }
    if (verifying) return;
    setVerifying(true);
    try {
      const { error: vErr } = await supabase.auth.verifyOtp({
        email: targetEmail,
        token,
        type: 'email',
      });
      if (vErr) {
        setError('Invalid or expired code. Try again.');
      }
      // onAuthStateChange handler will route after success
    } catch {
      setError('Unexpected error verifying code');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="modern-container-narrow">
      <div className="modern-card" style={{ maxWidth: 420, margin: '100px auto' }}>
        <h1 className="modern-title">Manager Login</h1>
        <p className="modern-subtitle" style={{ marginBottom: 16 }}>{codeSent ? 'Enter the 6-digit code in your inbox' : 'Sign in with a one-time code'}</p>

        {!codeSent ? (
          <form onSubmit={handleSendLink}>
            <div className="modern-mb-4">
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="modern-input"
              />
            </div>
            {error && <div className="modern-error" style={{ marginBottom: 8 }}>{error}</div>}
            {info && (
              <div
                className="modern-success"
                style={{
                  marginBottom: 8,
                  background: '#ecfdf5',
                  color: '#065f46',
                  border: '1px solid #10b981',
                  padding: '10px 12px',
                  borderRadius: 8,
                  fontWeight: 500
                }}
              >
                {info}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || cooldown > 0}
              className="modern-button"
              style={{
                width: '100%',
                background: '#059669',
                color: '#fff',
                fontWeight: 600,
                border: 'none',
                padding: '10px 16px',
                borderRadius: 8,
                boxShadow: '0 8px 16px rgba(5,150,105,0.25)'
              }}
            >
              {loading ? 'Sending…' : (cooldown > 0 ? `Please wait ${cooldown}s…` : 'Send Code')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <div className="modern-mb-4">
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>6-digit code:</label>
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="modern-input"
                placeholder="••••••"
                autoFocus
              />
            </div>
            {error && <div className="modern-error" style={{ marginBottom: 8 }}>{error}</div>}
            <button
              type="submit"
              disabled={verifying}
              className="modern-button"
              style={{
                width: '100%',
                background: '#2563eb',
                color: '#fff',
                fontWeight: 600,
                border: 'none',
                padding: '10px 16px',
                borderRadius: 8,
                boxShadow: '0 8px 16px rgba(37,99,235,0.25)'
              }}
            >
              {verifying ? 'Verifying…' : 'Verify Code'}
            </button>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={handleSendLink}
                disabled={loading || cooldown > 0}
                className="modern-button"
                style={{
                  background: (loading || cooldown > 0) ? '#9CA3AF' : '#374151',
                  color: '#fff',
                  fontWeight: 600,
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: 8,
                }}
              >
                {loading ? 'Sending…' : (cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code')}
              </button>
              <button
                type="button"
                onClick={() => { setCodeSent(false); setOtp(''); setInfo(''); setError(''); }}
                style={{ background: 'transparent', color: '#374151', border: 'none', padding: 8, cursor: 'pointer' }}
              >
                use different email
              </button>
            </div>
          </form>
        )}
        
      </div>
    </div>
  );
}

export default MagicLoginPage;


